import React, { useState, useEffect } from "react";
import { api } from "../api";
import Autosuggest from "react-autosuggest";
import { Field, useField } from "formik";

import AATextInput from "../ActiveAdmin/AATextInput";
import FieldFeedback, { errorMessages, useFieldFeedback } from "../ActiveAdmin/FieldFeedback";

/*
 * Input with autocompletion:
 * - can create new medicine (`createIfNotExists` param)
 * - can search across active ingredients (`withActiveIngredients` param)
 *
 * Expects `name` to be path to the medicine object, for example:
 * `prescription_medicines.${index}`
 */
const MedicinesInput = ({
  withActiveIngredients = false,
  createIfNotExists = false,
  label,
  name,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  // Локальное состояние для текста инпута, чтобы не триггерить AutoSave при вводе. Лекарства удалялись сами из списка, когда мы вручную вводили названия -> сервер не находил соответствий -> в результате лекарство удалялось на сервере и при обновлении страницы пункта с этим лекарством уже не было на фронте
  const [inputValue, setInputValue] = useState("");

  const [idField, idMeta, idHelpers] = useField(`${name}.medicineId`);
  const [_nameField, nameMeta, nameHelpers] = useField(`${name}.medicineName`);
  const [typeField, _typeMeta, typeHelpers] = useField(`${name}.medicineType`);
  const messages = [...errorMessages(nameMeta.error), ...errorMessages(idMeta.error)];
  const error = messages.length ? [...new Set(messages)].join(" · ") : undefined;
  const feedback = useFieldFeedback({}, error);

  // Синхронизируем локальное состояние с Formik при изменении извне
  useEffect(() => {
    setInputValue(nameMeta.value || "");
  }, [nameMeta.value]);

  const onSuggestionInputChange = (_, { newValue }) => {
    // Обновляем только локальное состояние, не Formik
    // Это предотвратит срабатывание AutoSave при вводе текста
    setInputValue(newValue);
    // Не обновляем Formik при вводе текста - только при выборе из списка
  };

  const onSuggestionsFetchRequested = async ({ value }) => {
    await api({
      method: "get",
      responseType: "json",
      url: "/api/medicines",
      params: {
        q: value,
        with_active_ingredients: withActiveIngredients,
      },
    }).then(
      (response) => {
        const results = response.data.results;
        if (createIfNotExists && !results.find((e) => e.name === value)) {
          const newSuggestion = { name: value, id: "", type: "", isNew: true };
          results.push(newSuggestion);
        }
        setSuggestions(results);
      },
      (error) => {
        alert(error);
      }
    );
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestionValue = (suggestion) => suggestion.name;

  const renderSuggestion = (suggestion) => {
    const label = suggestion.isNew
      ? `Создать: ${suggestion.name}`
      : suggestion.name;
    return <span>{label}</span>;
  };

  const createAndSelectNewSuggestion = (suggestion) => {
    api({
      method: "post",
      responseType: "json",
      url: "/api/medicines",
      data: {
        name: suggestion.name,
      },
    }).then(
      (response) => {
        const suggestion = response.data;
        // При создании нового лекарства обновляем Formik - это вызовет AutoSave
        idHelpers.setValue(suggestion.id);
        typeHelpers.setValue(suggestion.type);
        nameHelpers.setValue(suggestion.name);
        setInputValue(suggestion.name);
      },
      (error) => {
        alert(error);
      }
    );
  };

  const onSuggestionSelected = (_, { suggestion }) => {
    if (!suggestion.isNew) {
      // При выборе значения обновляем Formik - это вызовет AutoSave
      // Сначала обновляем имя, затем ID и тип, чтобы все изменения произошли вместе
      nameHelpers.setValue(suggestion.name);
      idHelpers.setValue(suggestion.id);
      typeHelpers.setValue(suggestion.type);
      setInputValue(suggestion.name);
    } else {
      createAndSelectNewSuggestion(suggestion);
    }
  };

  return (
    <React.Fragment>
      <li data-signal-field="" data-signal-label={label} data-signal-name={`${name}.medicineId`} data-signal-alias={`${name}.medicineName`}>
        <Field type="hidden" name={idField.name} />
        <Field type="hidden" name={typeField.name} />
        <label htmlFor={feedback.id}>{label}</label>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          onSuggestionSelected={onSuggestionSelected}
          inputProps={{
            ...feedback,
            value: inputValue,
            onChange: onSuggestionInputChange,
          }}
          highlightFirstSuggestion={true}
        />
        <FieldFeedback id={feedback.id} error={error} />
      </li>

      <AATextInput label='Дозировка' name={`${name}.dosage`} />
    </React.Fragment>
  );
};

export default MedicinesInput;
